/**
 * Shared validation translations for all monorepo projects
 * 
 * Usage in your app:
 * 
 * import { VALIDATION_TRANSLATIONS } from '@shared/i18n/validation-translations';
 * 
 * // In your transloco config or module:
 * // Merge these translations with your app-specific translations
 * 
 * const enTranslations = {
 *   ...yourAppTranslations,
 *   ...VALIDATION_TRANSLATIONS.en
 * };
 */

const validationEn = {
  "validation": {
    "required": {
      "field": "This field is required",
      "employer": "Employer is required",
      "position": "Position is required",
      "startDate": "Start date is required",
      "endDate": "End date is required",
      "description": "Description is required",
      "universityName": "University name is required",
      "fieldOfStudy": "Field of study is required",
      "highSchoolName": "School name is required",
      "language": "Language is required",
      "level": "Level is required",
      "skillName": "Skill name is required",
      "educationLevel": "Education level is required",
      "firstName": "First name is required",
      "lastName": "Last name is required",
      "email": "Email is required",
      "phone": "Phone number is required",
      "dateOfBirth": "Date of birth is required",
      "city": "City is required",
      "country": "Country is required",
      "streetName": "Street name is required",
      "zipCode": "ZIP code is required"
    },
    "maxLength": {
      "field": "This field cannot exceed {{max}} characters",
      "employer": "Employer name cannot exceed {{max}} characters",
      "position": "Position cannot exceed {{max}} characters",
      "description": "Description cannot exceed {{max}} characters",
      "city": "City cannot exceed {{max}} characters",
      "country": "Country cannot exceed {{max}} characters",
      "universityName": "University name cannot exceed {{max}} characters",
      "fieldOfStudy": "Field of study cannot exceed {{max}} characters",
      "highSchoolName": "School name cannot exceed {{max}} characters",
      "language": "Language cannot exceed {{max}} characters",
      "skillName": "Skill name cannot exceed {{max}} characters",
      "firstName": "First name cannot exceed {{max}} characters",
      "lastName": "Last name cannot exceed {{max}} characters",
      "streetName": "Street name cannot exceed {{max}} characters",
      "phone": "Phone number cannot exceed {{max}} characters"
    },
    "databaseConstraint": {
      "employer": "⚠️ ERROR: Employer name too long (max {{max}} characters) - Database constraint",
      "position": "⚠️ ERROR: Position too long (max {{max}} characters) - Database constraint",
      "description": "⚠️ ERROR: Description too long (max {{max}} characters) - Database constraint",
      "city": "⚠️ ERROR: City too long (max {{max}} characters) - Database constraint",
      "country": "⚠️ ERROR: Country too long (max {{max}} characters) - Database constraint",
      "universityName": "⚠️ ERROR: University name too long (max {{max}} characters) - Database constraint",
      "fieldOfStudy": "⚠️ ERROR: Field of study too long (max {{max}} characters) - Database constraint",
      "highSchoolName": "⚠️ ERROR: School name too long (max {{max}} characters) - Database constraint",
      "language": "⚠️ ERROR: Language name too long (max {{max}} characters) - Database constraint",
      "skillName": "⚠️ ERROR: Skill name too long (max {{max}} characters) - Database constraint"
    },
    "invalid": {
      "email": "Please enter a valid email address",
      "phone": "Please enter a valid phone number",
      "date": "Please enter a valid date",
      "url": "Please enter a valid URL",
      "number": "Please enter a valid number",
      "languageLevel": "Language level must be one of: A1, A2, B1, B2, C1, C2",
      "skillLevel": "Skill level must be one of: Beginner, Intermediate, Advanced, Expert"
    },
    "dateRange": {
      "startBeforeEnd": "Start date must be before end date",
      "endAfterStart": "End date must be after start date",
      "futureDate": "Date cannot be in the future",
      "invalidRange": "Invalid date range"
    },
    "fieldLabels": {
      "employer": "Employer",
      "position": "Position",
      "city": "City",
      "country": "Country",
      "startDate": "Start date",
      "endDate": "End date",
      "universityName": "University name",
      "fieldOfStudy": "Field of study",
      "highSchoolName": "School name",
      "language": "Language",
      "level": "Level",
      "skillName": "Skill name",
      "firstName": "First name",
      "lastName": "Last name",
      "email": "Email",
      "phone": "Phone",
      "dateOfBirth": "Date of birth",
      "streetName": "Street name",
      "streetNumber": "Street number",
      "zipCode": "ZIP code"
    }
  }
};

const validationSk = {
  "validation": {
    "required": {
      "field": "Toto pole je povinné",
      "employer": "Zamestnávateľ je povinný",
      "position": "Pozícia je povinná",
      "startDate": "Dátum začiatku je povinný",
      "endDate": "Dátum ukončenia je povinný",
      "universityName": "Názov univerzity je povinný",
      "fieldOfStudy": "Smer štúdia je povinný",
      "highSchoolName": "Názov školy je povinný",
      "language": "Jazyk je povinný",
      "level": "Úroveň je povinná",
      "skillName": "Názov zručnosti je povinný",
      "educationLevel": "Stupeň vzdelania je povinný",
      "firstName": "Meno je povinné",
      "lastName": "Priezvisko je povinné",
      "email": "Email je povinný",
      "phone": "Telefónne číslo je povinné",
      "dateOfBirth": "Dátum narodenia je povinný",
      "city": "Mesto je povinné",
      "country": "Krajina je povinná",
      "streetName": "Názov ulice je povinný",
      "zipCode": "PSČ je povinné"
    },
    "maxLength": {
      "field": "Toto pole nemôže presiahnuť {{max}} znakov",
      "employer": "Názov zamestnávateľa nemôže presiahnuť {{max}} znakov",
      "position": "Pozícia nemôže presiahnuť {{max}} znakov",
      "city": "Mesto nemôže presiahnuť {{max}} znakov",
      "country": "Krajina nemôže presiahnuť {{max}} znakov",
      "universityName": "Názov univerzity nemôže presiahnuť {{max}} znakov",
      "fieldOfStudy": "Smer štúdia nemôže presiahnuť {{max}} znakov",
      "highSchoolName": "Názov školy nemôže presiahnuť {{max}} znakov",
      "language": "Jazyk nemôže presiahnuť {{max}} znakov",
      "skillName": "Názov zručnosti nemôže presiahnuť {{max}} znakov",
      "firstName": "Meno nemôže presiahnuť {{max}} znakov",
      "lastName": "Priezvisko nemôže presiahnuť {{max}} znakov",
      "streetName": "Názov ulice nemôže presiahnuť {{max}} znakov",
      "phone": "Telefónne číslo nemôže presiahnuť {{max}} znakov"
    },
    "databaseConstraint": {
      "employer": "⚠️ CHYBA: Názov zamestnávateľa je príliš dlhý (max {{max}} znakov) - Obmedzenie databázy",
      "position": "⚠️ CHYBA: Pozícia je príliš dlhá (max {{max}} znakov) - Obmedzenie databázy",
      "city": "⚠️ CHYBA: Mesto je príliš dlhé (max {{max}} znakov) - Obmedzenie databázy",
      "country": "⚠️ CHYBA: Krajina je príliš dlhá (max {{max}} znakov) - Obmedzenie databázy",
      "universityName": "⚠️ CHYBA: Názov univerzity je príliš dlhý (max {{max}} znakov) - Obmedzenie databázy",
      "fieldOfStudy": "⚠️ CHYBA: Smer štúdia je príliš dlhý (max {{max}} znakov) - Obmedzenie databázy",
      "highSchoolName": "⚠️ CHYBA: Názov školy je príliš dlhý (max {{max}} znakov) - Obmedzenie databázy",
      "language": "⚠️ CHYBA: Názov jazyka je príliš dlhý (max {{max}} znakov) - Obmedzenie databázy",
      "skillName": "⚠️ CHYBA: Názov zručnosti je príliš dlhý (max {{max}} znakov) - Obmedzenie databázy"
    },
    "invalid": {
      "email": "Zadajte platnú emailovú adresu",
      "phone": "Zadajte platné telefónne číslo",
      "date": "Zadajte platný dátum",
      "url": "Zadajte platnú URL adresu",
      "number": "Zadajte platné číslo",
      "languageLevel": "Úroveň jazyka musí byť jedna z: A1, A2, B1, B2, C1, C2",
      "skillLevel": "Úroveň zručnosti musí byť jedna z: Začiatočník, Stredne pokročilý, Pokročilý, Expert"
    },
    "dateRange": {
      "startBeforeEnd": "Dátum začiatku musí byť pred dátumom ukončenia",
      "endAfterStart": "Dátum ukončenia musí byť po dátume začiatku",
      "futureDate": "Dátum nemôže byť v budúcnosti",
      "invalidRange": "Neplatný rozsah dátumov"
    },
    "fieldLabels": {
      "employer": "Zamestnávateľ",
      "position": "Pozícia",
      "city": "Mesto",
      "country": "Krajina",
      "startDate": "Dátum začiatku",
      "endDate": "Dátum ukončenia",
      "universityName": "Názov univerzity",
      "fieldOfStudy": "Smer štúdia",
      "highSchoolName": "Názov školy",
      "language": "Jazyk",
      "level": "Úroveň",
      "skillName": "Názov zručnosti",
      "firstName": "Meno",
      "lastName": "Priezvisko",
      "email": "Email",
      "phone": "Telefón",
      "dateOfBirth": "Dátum narodenia",
      "streetName": "Ulica",
      "streetNumber": "Číslo domu",
      "zipCode": "PSČ"
    }
  }
};

const validationCz = {
  "validation": {
    "required": {
      "field": "Toto pole je povinné",
      "employer": "Zaměstnavatel je povinný",
      "position": "Pozice je povinná",
      "startDate": "Datum začátku je povinné",
      "endDate": "Datum ukončení je povinné",
      "universityName": "Název univerzity je povinný",
      "fieldOfStudy": "Studijní obor je povinný",
      "highSchoolName": "Název školy je povinný",
      "language": "Jazyk je povinný",
      "level": "Úroveň je povinná",
      "skillName": "Název dovednosti je povinný",
      "educationLevel": "Stupeň vzdělání je povinný",
      "firstName": "Jméno je povinné",
      "lastName": "Příjmení je povinné",
      "email": "Email je povinný",
      "phone": "Telefonní číslo je povinné",
      "dateOfBirth": "Datum narození je povinné",
      "city": "Město je povinné",
      "country": "Země je povinná",
      "streetName": "Název ulice je povinný",
      "zipCode": "PSČ je povinné"
    },
    "maxLength": {
      "field": "Toto pole nemůže přesáhnout {{max}} znaků",
      "employer": "Název zaměstnavatele nemůže přesáhnout {{max}} znaků",
      "position": "Pozice nemůže přesáhnout {{max}} znaků",
      "city": "Město nemůže přesáhnout {{max}} znaků",
      "country": "Země nemůže přesáhnout {{max}} znaků",
      "universityName": "Název univerzity nemůže přesáhnout {{max}} znaků",
      "fieldOfStudy": "Studijní obor nemůže přesáhnout {{max}} znaků",
      "highSchoolName": "Název školy nemůže přesáhnout {{max}} znaků",
      "language": "Jazyk nemůže přesáhnout {{max}} znaků",
      "skillName": "Název dovednosti nemůže přesáhnout {{max}} znaků",
      "firstName": "Jméno nemůže přesáhnout {{max}} znaků",
      "lastName": "Příjmení nemůže přesáhnout {{max}} znaků",
      "streetName": "Název ulice nemůže přesáhnout {{max}} znaků",
      "phone": "Telefonní číslo nemůže přesáhnout {{max}} znaků"
    },
    "databaseConstraint": {
      "employer": "⚠️ CHYBA: Název zaměstnavatele je příliš dlouhý (max {{max}} znaků) - Omezení databáze",
      "position": "⚠️ CHYBA: Pozice je příliš dlouhá (max {{max}} znaků) - Omezení databáze",
      "city": "⚠️ CHYBA: Město je příliš dlouhé (max {{max}} znaků) - Omezení databáze",
      "country": "⚠️ CHYBA: Země je příliš dlouhá (max {{max}} znaků) - Omezení databáze",
      "universityName": "⚠️ CHYBA: Název univerzity je příliš dlouhý (max {{max}} znaků) - Omezení databáze",
      "fieldOfStudy": "⚠️ CHYBA: Studijní obor je příliš dlouhý (max {{max}} znaků) - Omezení databáze",
      "highSchoolName": "⚠️ CHYBA: Název školy je příliš dlouhý (max {{max}} znaků) - Omezení databáze",
      "language": "⚠️ CHYBA: Název jazyka je příliš dlouhý (max {{max}} znaků) - Omezení databáze",
      "skillName": "⚠️ CHYBA: Název dovednosti je příliš dlouhý (max {{max}} znaků) - Omezení databáze"
    },
    "invalid": {
      "email": "Zadejte platnou emailovou adresu",
      "phone": "Zadejte platné telefonní číslo",
      "date": "Zadejte platné datum",
      "url": "Zadejte platnou URL adresu",
      "number": "Zadejte platné číslo",
      "languageLevel": "Úroveň jazyka musí být jedna z: A1, A2, B1, B2, C1, C2",
      "skillLevel": "Úroveň dovednosti musí být jedna z: Začátečník, Středně pokročilý, Pokročilý, Expert"
    },
    "dateRange": {
      "startBeforeEnd": "Datum začátku musí být před datem ukončení",
      "endAfterStart": "Datum ukončení musí být po datu začátku",
      "futureDate": "Datum nemůže být v budoucnosti",
      "invalidRange": "Neplatný rozsah dat"
    },
    "fieldLabels": {
      "employer": "Zaměstnavatel",
      "position": "Pozice",
      "city": "Město",
      "country": "Země",
      "startDate": "Datum začátku",
      "endDate": "Datum ukončení",
      "universityName": "Název univerzity",
      "fieldOfStudy": "Studijní obor",
      "highSchoolName": "Název školy",
      "language": "Jazyk",
      "level": "Úroveň",
      "skillName": "Název dovednosti",
      "firstName": "Jméno",
      "lastName": "Příjmení",
      "email": "Email",
      "phone": "Telefon",
      "dateOfBirth": "Datum narození",
      "streetName": "Ulice",
      "streetNumber": "Číslo domu",
      "zipCode": "PSČ"
    }
  }
};

export const VALIDATION_TRANSLATIONS = {
  en: validationEn,
  sk: validationSk,
  cz: validationCz
} as const;

/**
 * Helper function to get validation translation key
 * @param type - Type of validation (required, maxLength, databaseConstraint, invalid, dateRange)
 * @param field - Field name
 * @returns Translation key string
 */
export function getValidationTranslationKey(type: string, field: string): string {
  return `validation.${type}.${field}`;
}

/**
 * Helper function to get field label translation key
 * @param field - Field name
 * @returns Translation key string
 */
export function getFieldLabelTranslationKey(field: string): string {
  return `validation.fieldLabels.${field}`;
}